/**
 * Account settings — global, not org-scoped.
 *
 * Lives at /settings. The sidebar's user menu also routes here. Today
 * it covers theme + sign-out; future: profile, notification prefs,
 * personal API tokens.
 */
import { useState } from 'react';
import { updatePassword } from 'aws-amplify/auth';
import { Sun, Moon, Monitor, User as UserIcon, Lock, Loader2, CheckCircle } from 'lucide-react';
import { useTheme } from '@/providers/theme-provider';
import { useAuth } from '@/providers/auth-provider';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { cn, titleCaseName } from '@/lib/utils';

const THEMES: Array<{ value: 'light' | 'dark' | 'system'; label: string; icon: React.ComponentType<{ className?: string }>; description: string }> = [
  { value: 'light',  label: 'Light',  icon: Sun,     description: 'Always use the light palette.' },
  { value: 'dark',   label: 'Dark',   icon: Moon,    description: 'Always use the midnight-indigo palette.' },
  { value: 'system', label: 'System', icon: Monitor, description: "Match your OS preference." },
];

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setIsLoading(true);
    try {
      await updatePassword({ oldPassword: currentPassword, newPassword });
      setSuccess(true);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      if (msg.includes('Incorrect username or password') || msg.includes('NotAuthorizedException')) {
        setError('Current password is incorrect.');
      } else if (msg.includes('Password does not conform') || msg.includes('InvalidPasswordException')) {
        setError('New password must include upper- and lower-case letters, a number, and be at least 8 characters.');
      } else if (msg.includes('LimitExceededException') || msg.includes('Attempt limit exceeded')) {
        setError('Too many attempts. Please wait a few minutes before trying again.');
      } else {
        setError(`Could not update password: ${msg}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Card className="p-5">
      <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Change password</p>

      {success && (
        <div className="flex items-center gap-2 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 text-green-700 dark:text-green-400 text-sm rounded-lg px-4 py-3 mb-4">
          <CheckCircle className="h-4 w-4 shrink-0" />
          Password updated successfully.
        </div>
      )}

      {error && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-500 text-red-700 dark:text-red-400 text-sm rounded-lg px-4 py-3 mb-4">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="currentPassword">Current password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="currentPassword"
              type="password"
              placeholder="--------"
              className="pl-10"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="newPassword">New password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="newPassword"
              type="password"
              placeholder="At least 8 characters"
              className="pl-10"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
          <p className="text-xs text-muted-foreground">Must include upper- and lower-case letters and a number.</p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm new password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Re-enter new password"
              className="pl-10"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </div>
        </div>

        <Button type="submit" disabled={isLoading}>
          {isLoading ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Updating...
            </>
          ) : (
            'Update password'
          )}
        </Button>
      </form>
    </Card>
  );
}

export function SettingsPage() {
  const { theme, setTheme } = useTheme();
  const { user } = useAuth();

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-foreground">Account Settings</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Personal preferences for your account. Org-scoped settings live under <span className="font-mono text-foreground">Org Settings</span> in the sidebar.
        </p>
      </div>

      {/* Identity */}
      <Card className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Signed in as</p>
        <div className="flex items-center gap-3">
          <div className="h-10 w-10 rounded-full bg-primary/15 flex items-center justify-center">
            <UserIcon className="h-5 w-5 text-primary-strong" />
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">{titleCaseName(user?.displayName) || user?.email || 'You'}</p>
            {user?.email && <p className="text-xs text-muted-foreground truncate">{user.email}</p>}
          </div>
        </div>
      </Card>

      {/* Change password */}
      <ChangePasswordCard />

      {/* Appearance */}
      <Card className="p-5">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Appearance</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
          {THEMES.map((t) => {
            const checked = theme === t.value;
            const Icon = t.icon;
            return (
              <button
                key={t.value}
                type="button"
                onClick={() => setTheme(t.value)}
                className={cn(
                  'flex items-start gap-3 px-3 py-3 rounded-lg border text-left transition-colors',
                  checked
                    ? 'border-primary/40 bg-primary/5'
                    : 'border-border hover:bg-muted/40',
                )}
              >
                <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', checked ? 'text-primary' : 'text-muted-foreground')} />
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-foreground">{t.label}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{t.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </Card>

    </div>
  );
}
